import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TextField, InputAdornment } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import DivDiagonal from '../../components/DivDiagonal';
import CustomButton from '../../components/Form/Button';
import CustomSaleItemModal from './CustomSaleItemModal';
import {
  SalesWrapper,
  ContentArea,
  ProductsSection,
  CartSection,
  SectionTitle,
  CustomSaleButton,
  SearchCard,
  SearchRow,
  FormWrapper,
  SearchInput,
  ProductsGrid,
  ProductCard,
  ProductImage,
  ProductInfo,
  ProductName,
  ProductBrandStock,
  ProductPrice,
  ProductStock,
  CartCard,
  CartHeader,
  ClearCartButton,
  CartItems,
  CartItem,
  ItemInfo,
  ItemName,
  ItemDetails,
  ItemQuantity,
  QuantityButton,
  ItemTotal,
  RemoveButton,
  CartFooter,
  DiscountSection,
  DiscountLabel,
  DiscountInputWrapper,
  DiscountTypeToggle,
  DiscountTypeButton,
  DiscountInput,
  ObservationSection,
  ObservationHeader,
  ObservationLabel,
  ObservationToggleSection,
  ObservationToggleButton,
  ObservationTextarea,
  TotalsSection,
  TotalRow,
  TotalLabel,
  TotalValue,
  ActionButtons,
  ActionButton,
  EmptyCartMessage,
  EmptyProductsMessage
} from './styles';
import { FaShoppingCart, FaCheckCircle, FaTimes, FaPlus, FaMinus, FaTrash, FaBroom, FaMoneyBillWave } from 'react-icons/fa';
import { MdImage } from 'react-icons/md';
import { getProductsForSale, createSale } from '../../services/Sale';
import { currencyFormat, parseCurrencyToNumber } from '../../utils/masks';

// Função auxiliar para formatar valores monetários corretamente
const formatCurrency = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

function Sales() {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Carregar carrinho do localStorage na inicialização
  const loadCartFromStorage = () => {
    try {
      const savedCart = localStorage.getItem('sales_cart');
      if (savedCart) {
        return JSON.parse(savedCart);
      }
      return [];
    } catch (error) {
      console.error('Erro ao carregar carrinho do localStorage:', error);
      return [];
    }
  };
  
  const [cartItems, setCartItems] = useState(loadCartFromStorage);
  const [discount, setDiscount] = useState(() => {
    try {
      const savedDiscount = localStorage.getItem('sales_discount');
      return savedDiscount ? parseFloat(savedDiscount) : 0;
    } catch (error) {
      return 0;
    }
  });
  const [discountType, setDiscountType] = useState(() => {
    try {
      const savedType = localStorage.getItem('sales_discount_type');
      return savedType || 'value'; // 'value' ou 'percentage'
    } catch (error) {
      return 'value';
    }
  });
  const [observation, setObservation] = useState(() => {
    try {
      const savedObservation = localStorage.getItem('sales_observation');
      return savedObservation || '';
    } catch (error) {
      return '';
    }
  });
  const [showObservation, setShowObservation] = useState(() => {
    try {
      const savedObservation = localStorage.getItem('sales_observation');
      return !!savedObservation; // Mostra se já existe observação salva
    } catch (error) {
      return false;
    }
  });
  const [addedProductId, setAddedProductId] = useState(null); // Para animação visual
  const [cartPulse, setCartPulse] = useState(false); // Para animação no carrinho
  const [customSaleModalOpen, setCustomSaleModalOpen] = useState(false);

  // Trava síncrona contra leituras de código de barras concorrentes (ex: scanner duplicando leitura)
  const isScanningRef = useRef(false);

  // Referência ao campo de busca para poder focá-lo novamente após finalizar uma venda
  const searchInputRef = useRef(null);
  const wasLoadingRef = useRef(false);

  // Sempre que o processamento da venda terminar, devolve o foco ao campo de busca
  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      searchInputRef.current?.focus();
    }
    wasLoadingRef.current = loading;
  }, [loading]);

  // Salvar carrinho no localStorage sempre que houver mudanças
  useEffect(() => {
    try {
      localStorage.setItem('sales_cart', JSON.stringify(cartItems));
    } catch (error) {
      console.error('Erro ao salvar carrinho no localStorage:', error);
    }
  }, [cartItems]);
  
  // Salvar desconto no localStorage sempre que houver mudanças
  useEffect(() => {
    try {
      localStorage.setItem('sales_discount', discount.toString());
    } catch (error) {
      console.error('Erro ao salvar desconto no localStorage:', error);
    }
  }, [discount]);
  
  // Salvar tipo de desconto no localStorage sempre que houver mudanças
  useEffect(() => {
    try {
      localStorage.setItem('sales_discount_type', discountType);
    } catch (error) {
      console.error('Erro ao salvar tipo de desconto no localStorage:', error);
    }
  }, [discountType]);
  
  // Salvar observação no localStorage sempre que houver mudanças
  useEffect(() => {
    try {
      localStorage.setItem('sales_observation', observation);
    } catch (error) {
      console.error('Erro ao salvar observação no localStorage:', error);
    }
  }, [observation]);

  const searchProducts = useCallback(async () => {
    setSearchLoading(true);
    try {
      const productsData = await getProductsForSale(searchTerm);
      setProducts(productsData);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm]);

  // Buscar produtos com debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchProducts();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchProducts]);

  // Processar código de barras lido (quando Enter é pressionado)
  const handleBarcodeScan = async (e) => {
    if (e.key !== 'Enter' || !searchTerm.trim()) {
      return;
    }
    e.preventDefault();

    // Trava síncrona: ignora leituras concorrentes (scanner duplicando, Enter repetido, etc.)
    if (isScanningRef.current || loading) {
      return;
    }
    isScanningRef.current = true;
    setSearchLoading(true);

    try {
      const productsData = await getProductsForSale(searchTerm.trim());

      if (productsData.length === 1) {
        // Encontrou exatamente um produto
        const product = productsData[0];

        // Verificar se é serviço e carrinho está vazio
        const isService = product.type === 'SERVICE';
        const isCartEmpty = cartItems.length === 0;

        if (isService && isCartEmpty) {
          // Adicionar e finalizar automaticamente, sem depender de setState assíncrono
          const newItem = {
            id: `temp_${Date.now()}`,
            product,
            quantity: 1,
            unitPrice: product.salePrice || product.costPrice || 0,
            total: product.salePrice || product.costPrice || 0
          };
          setSearchTerm(''); // Limpar campo de busca
          setCartItems([newItem]);
          const wasSold = await handleFinishSaleWithItems([newItem]);
          if (!wasSold) {
            // A venda automática falhou (ex.: bloqueio de duplicidade, erro de rede).
            // Reverte o carrinho para vazio para não deixar o item "preso" e permitir tentar de novo.
            setCartItems([]);
          }
        } else {
          // Comportamento normal: apenas adicionar ao carrinho
          addToCart(product);
          setSearchTerm(''); // Limpar campo de busca
          if (isService) {
            toast.info(`Serviço "${product.name}" adicionado ao carrinho`);
          } else {
            toast.success(`Produto "${product.name}" adicionado ao carrinho`);
          }
        }
      } else if (productsData.length > 1) {
        // Múltiplos produtos encontrados - comportamento normal
        toast.info(`${productsData.length} produtos encontrados`);
      } else {
        // Nenhum produto encontrado
        toast.warning('Produto não encontrado');
      }
    } catch (error) {
      console.error('Erro ao processar código de barras:', error);
      toast.error('Erro ao buscar produto');
    } finally {
      setSearchLoading(false);
      isScanningRef.current = false;
    }
  };

  // Adicionar produto ao carrinho
  const addToCart = (product, showFeedback = true) => {
    const isService = product.type === 'SERVICE';

    if (!isService && product.stock <= 0) {
      toast.warning('Produto sem estoque disponível');
      return;
    }

    const existingItem = cartItems.find(item => !item.isCustom && item.product.id === product.id);

    if (existingItem) {
      if (!isService && existingItem.quantity >= product.stock) {
        toast.warning('Quantidade máxima em estoque atingida');
        return;
      }
      updateQuantity(existingItem.id, existingItem.quantity + 1);

      // Feedback visual
      if (showFeedback) {
        setAddedProductId(product.id);
        setCartPulse(true);
        setTimeout(() => {
          setAddedProductId(null);
          setCartPulse(false);
        }, 600);
        toast.success(`${product.name} - Quantidade atualizada!`, {
          icon: '🛒',
          autoClose: 2000
        });
      }
    } else {
      const newItem = {
        id: `temp_${Date.now()}`,
        product: product,
        quantity: 1,
        unitPrice: product.salePrice || product.costPrice || 0,
        total: product.salePrice || product.costPrice || 0
      };

      setCartItems(prevItems => [...prevItems, newItem]);

      // Feedback visual
      if (showFeedback) {
        setAddedProductId(product.id);
        setCartPulse(true);
        setTimeout(() => {
          setAddedProductId(null);
          setCartPulse(false);
        }, 600);
        toast.success(`${product.name} adicionado ao carrinho!`, {
          icon: '✅',
          autoClose: 2000
        });
      }
    }
  };

  // Adicionar item avulso (sem produto vinculado) ao carrinho
  const handleAddCustomItem = (description, amount) => {
    const value = parseCurrencyToNumber(amount);
    if (value <= 0) {
      toast.warning('Informe um valor válido.');
      return;
    }

    const desc = description.trim() || 'Venda avulsa';
    const newItem = {
      id: `custom_${Date.now()}`,
      product: null,
      isCustom: true,
      description: desc,
      quantity: 1,
      unitPrice: value,
      total: value
    };

    setCartItems(prevItems => [...prevItems, newItem]);
    setCartPulse(true);
    setTimeout(() => setCartPulse(false), 600);
    toast.success(`${desc} adicionado ao carrinho`, { icon: '✅', autoClose: 2000 });
  };

  // Finalizar venda com itens específicos (para venda automática)
  // Retorna true se a venda foi concluída, false caso contrário (para o chamador decidir se deve reverter o carrinho)
  const handleFinishSaleWithItems = async (itemsToSell) => {
    if (itemsToSell.length === 0 || loading) {
      return false;
    }

    setLoading(true);
    try {
      const subtotal = itemsToSell.reduce((sum, item) => sum + item.total, 0);
      let discountValue = discount;

      // Se o desconto for em porcentagem, calcular o valor
      if (discountType === 'percentage') {
        discountValue = (subtotal * discount) / 100;
      }

      const total = subtotal - discountValue;

      const saleData = {
        items: itemsToSell.map(item => (
          item.isCustom
            ? { productId: null, description: item.description, quantity: item.quantity, unitPrice: item.unitPrice }
            : { productId: item.product.id, quantity: item.quantity, unitPrice: item.unitPrice }
        )),
        subtotal: subtotal,
        discount: discountValue,
        total: total,
        observation: observation || null
      };

      const response = await createSale(saleData);

      if (response && response.status === 200) {
        toast.success('Venda realizada com sucesso!');
        setCartItems([]);
        setDiscount(0);
        setDiscountType('value');
        setObservation('');
        setSearchTerm('');
        // Limpar localStorage após venda bem-sucedida
        localStorage.removeItem('sales_cart');
        localStorage.removeItem('sales_discount');
        localStorage.removeItem('sales_discount_type');
        localStorage.removeItem('sales_observation');
        searchProducts();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao finalizar venda:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar quantidade de um item
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    setCartItems(cartItems.map(item => {
      if (item.id === itemId) {
        if (!item.isCustom && item.product.type !== 'SERVICE' && newQuantity > item.product.stock) {
          toast.warning('Quantidade máxima em estoque atingida');
          return item;
        }
        const total = newQuantity * item.unitPrice;
        return { ...item, quantity: newQuantity, total };
      }
      return item;
    }));
  };

  // Remover item do carrinho
  const removeFromCart = (itemId) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };

  // Calcular totais
  const calculateTotals = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
    let discountValue = discount;
    
    // Se o desconto for em porcentagem, calcular o valor
    if (discountType === 'percentage') {
      discountValue = (subtotal * discount) / 100;
    }
    
    const total = subtotal - discountValue;
    return { subtotal, total, discountValue };
  };

  // Finalizar venda
  const handleFinishSale = async () => {
    if (loading) {
      return;
    }

    if (cartItems.length === 0) {
      toast.warning('Adicione pelo menos um produto ao carrinho');
      return;
    }

    setLoading(true);
    try {
      const { subtotal, total, discountValue } = calculateTotals();
      
      const saleData = {
        items: cartItems.map(item => (
          item.isCustom
            ? { productId: null, description: item.description, quantity: item.quantity, unitPrice: item.unitPrice }
            : { productId: item.product.id, quantity: item.quantity, unitPrice: item.unitPrice }
        )),
        subtotal: subtotal,
        discount: discountValue,
        total: total,
        observation: observation || null
      };

      const response = await createSale(saleData);
      
      if (response && response.status === 200) {
        toast.success('Venda realizada com sucesso!');
        setCartItems([]);
        setDiscount(0);
        setDiscountType('value');
        setObservation('');
        setSearchTerm('');
        // Limpar localStorage após venda bem-sucedida
        localStorage.removeItem('sales_cart');
        localStorage.removeItem('sales_discount');
        localStorage.removeItem('sales_discount_type');
        localStorage.removeItem('sales_observation');
        searchProducts();
      }
    } catch (error) {
      console.error('Erro ao finalizar venda:', error);
    } finally {
      setLoading(false);
    }
  };

  // Limpar carrinho
  const handleClearCart = () => {
    if (cartItems.length === 0) {
      return;
    }
    
      setCartItems([]);
      setDiscount(0);
      setDiscountType('value');
      setObservation('');
      // Limpar localStorage
      localStorage.removeItem('sales_cart');
      localStorage.removeItem('sales_discount');
      localStorage.removeItem('sales_discount_type');
      localStorage.removeItem('sales_observation');
      toast.info('Carrinho limpo');
  };


  const { subtotal, total, discountValue } = calculateTotals();

  return (
    <SalesWrapper>
      <DivDiagonal />
      
      <ContentArea>

        <div className="row" style={{ margin: 0, height: '100%', maxHeight: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
          {/* Seção de Produtos */}
          <div className="col-lg-8" style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', maxHeight: '100%' }}>
            <ProductsSection>
              <SectionTitle>
                <i className="fas fa-box" /> Produtos Disponíveis
              </SectionTitle>

              <SearchCard style={{ boxShadow: 'none' }}>
                <SearchRow>
                  <FormWrapper style={{ flex: 1 }}>
                    <SearchInput
                      fullWidth
                      inputRef={searchInputRef}
                      label={loading ? 'Finalizando venda, aguarde...' : 'Buscar produto por nome ou código'}
                      value={searchTerm}
                      disabled={loading}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleBarcodeScan}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <i className="fas fa-search" style={{ color: '#ffffff' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormWrapper>
                  <CustomSaleButton type="button" onClick={() => setCustomSaleModalOpen(true)} disabled={loading}>
                    <FaMoneyBillWave /> Venda Avulsa
                  </CustomSaleButton>
                </SearchRow>
              </SearchCard>

              <ProductsGrid>
                {searchLoading ? (
                  <EmptyProductsMessage>
                    <i className="fas fa-spinner fa-spin" />
                    <p>Buscando produtos...</p>
                  </EmptyProductsMessage>
                ) : products.length === 0 ? (
                  <EmptyProductsMessage>
                    <i className="fas fa-box-open" />
                    <p>Nenhum produto encontrado</p>
                    <span>Use a busca para encontrar produtos</span>
                  </EmptyProductsMessage>
                ) : (
                  products.map((product) => {
                    const isService = product.type === 'SERVICE';
                    const outOfStock = !isService && product.stock <= 0;
                    return (
                    <ProductCard
                      key={product.id}
                      onClick={() => !loading && addToCart(product)}
                      data-disabled={outOfStock ? "true" : "false"}
                      data-added={addedProductId === product.id ? "true" : "false"}
                      title={outOfStock ? 'Sem estoque disponível para venda' : undefined}
                    >
                      <ProductImage>
                        {product.urlPhoto ? (
                          <img
                            src={`${process.env.REACT_APP_API_ENDPOINT}${product.urlPhoto}`}
                            alt={product.name}
                          />
                        ) : (
                          <MdImage />
                        )}
                      </ProductImage>
                      <ProductInfo>
                        <ProductName title={product.name}>{product.name}</ProductName>
                        <ProductBrandStock>
                          {product.brand ? (
                            <>
                              <span>{product.brand}</span>
                              {!isService && (
                                <>
                                  <span className="separator">|</span>
                                  <ProductStock className={product.stock === 0 ? 'out-of-stock' : product.stock < 5 ? 'low-stock' : ''}>
                                    Qtd: {product.stock}
                                  </ProductStock>
                                </>
                              )}
                            </>
                          ) : !isService ? (
                            <ProductStock className={product.stock === 0 ? 'out-of-stock' : product.stock < 5 ? 'low-stock' : ''}>
                              Qtd: {product.stock}
                            </ProductStock>
                          ) : null}
                        </ProductBrandStock>
                        <ProductPrice>
                          {formatCurrency(product.salePrice || product.costPrice || 0)}
                        </ProductPrice>
                      </ProductInfo>
                    </ProductCard>
                    );
                  })
                )}
              </ProductsGrid>
            </ProductsSection>
          </div>

          {/* Seção do Carrinho */}
          <div className="col-lg-4" style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', maxHeight: '100%' }}>
            <CartSection>
              <CartCard style={{ boxShadow: 'none' }}>
                <CartHeader data-pulse={cartPulse ? "true" : "false"}>
                  <h3>
                    <FaShoppingCart /> Carrinho de Vendas {cartItems.length > 0 && `(${cartItems.length})`}
                  </h3>
                  <ClearCartButton onClick={handleClearCart} disabled={cartItems.length === 0}>
                    <FaBroom /> Limpar
                  </ClearCartButton>
                </CartHeader>

                <CartItems>
                  {cartItems.length === 0 ? (
                    <EmptyCartMessage>
                      <i className="fas fa-shopping-cart" />
                      <p>Carrinho vazio</p>
                      <span>Adicione produtos para iniciar a venda</span>
                    </EmptyCartMessage>
                  ) : (
                    cartItems.map((item) => (
                      <CartItem key={item.id}>
                        <ItemInfo>
                          <ItemName>
                            {item.isCustom
                              ? item.description
                              : (item.product.brand
                                ? `${item.product.name} - ${item.product.brand}`
                                : item.product.name)
                            }
                          </ItemName>
                          <ItemDetails>
                            <ItemQuantity>
                              <QuantityButton onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                                <FaMinus />
                              </QuantityButton>
                              <span>{item.quantity}</span>
                              <QuantityButton onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                                <FaPlus />
                              </QuantityButton>
                            </ItemQuantity>
                            <ItemTotal>{formatCurrency(item.total)}</ItemTotal>
                          </ItemDetails>
                        </ItemInfo>
                        <RemoveButton onClick={() => removeFromCart(item.id)}>
                          <FaTrash />
                        </RemoveButton>
                      </CartItem>
                    ))
                  )}
                </CartItems>

                <CartFooter>
                  <DiscountSection>
                    <DiscountLabel>Desconto:</DiscountLabel>
                    <DiscountInputWrapper>
                      <DiscountTypeToggle>
                        <DiscountTypeButton 
                          active={discountType === 'value'} 
                          onClick={() => setDiscountType('value')}
                        >
                          R$
                        </DiscountTypeButton>
                        <DiscountTypeButton 
                          active={discountType === 'percentage'} 
                          onClick={() => setDiscountType('percentage')}
                        >
                          %
                        </DiscountTypeButton>
                      </DiscountTypeToggle>
                      <DiscountInput
                        type="number"
                        min="0"
                        step={discountType === 'percentage' ? '0.1' : '0.01'}
                        max={discountType === 'percentage' ? 100 : subtotal}
                        value={discount || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          if (discountType === 'percentage') {
                            if (value < 0) {
                              setDiscount(0);
                            } else if (value > 100) {
                              setDiscount(100);
                              toast.warning('Desconto máximo: 100%');
                            } else {
                              setDiscount(value);
                            }
                          } else {
                            const maxDiscount = subtotal;
                            if (value < 0) {
                              setDiscount(0);
                            } else if (value > maxDiscount) {
                              setDiscount(maxDiscount);
                              toast.warning(`Desconto máximo: ${formatCurrency(maxDiscount)}`);
                            } else {
                              setDiscount(value);
                            }
                          }
                        }}
                        placeholder={discountType === 'percentage' ? '0' : '0,00'}
                      />
                    </DiscountInputWrapper>
                  </DiscountSection>
                  
                  {showObservation ? (
                    <ObservationSection>
                      <ObservationHeader>
                        <ObservationLabel>Observação:&nbsp;</ObservationLabel>
                        <ObservationToggleButton onClick={() => setShowObservation(false)}>
                          <FaTimes />
                        </ObservationToggleButton>
                      </ObservationHeader>
                      <ObservationTextarea
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        placeholder="Adicione uma observação sobre esta venda (opcional)"
                        rows={2}
                      />
                    </ObservationSection>
                  ) : (
                    <ObservationToggleSection>
                      <ObservationToggleButton onClick={() => setShowObservation(true)}>
                        <FaPlus /> Adicionar observação
                      </ObservationToggleButton>
                    </ObservationToggleSection>
                  )}
                  
                  <TotalsSection>
                    <TotalRow>
                      <TotalLabel>Subtotal:</TotalLabel>
                      <TotalValue>{formatCurrency(subtotal)}</TotalValue>
                    </TotalRow>
                    <TotalRow style={{ paddingTop: '0px' }}>
                      <TotalLabel>Desconto:</TotalLabel>
                      <TotalValue discount>
                        {discountType === 'percentage' 
                          ? `${discount}% ( - ${formatCurrency(discountValue)})`
                          : `- ${formatCurrency(discountValue)}`
                        }
                      </TotalValue>
                    </TotalRow>
                    <TotalRow total>
                      <TotalLabel>Total:</TotalLabel>
                      <TotalValue total>{formatCurrency(total)}</TotalValue>
                    </TotalRow>
                  </TotalsSection>

                  <ActionButtons>
                    <ActionButton primary onClick={handleFinishSale} disabled={cartItems.length === 0 || loading}>
                      <FaCheckCircle />
                      {loading ? 'Processando...' : 'Finalizar Venda'}
                    </ActionButton>
                  </ActionButtons>
                </CartFooter>
              </CartCard>
            </CartSection>
          </div>
        </div>
      </ContentArea>

      <CustomSaleItemModal
        isOpen={customSaleModalOpen}
        onClose={() => setCustomSaleModalOpen(false)}
        onConfirm={handleAddCustomItem}
      />

    </SalesWrapper>
  );
}

export default Sales;
